import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getTeacherTrails } from '../../services/learningTrailService';

import TrailSelector from './components/TrailSelector';
import QRCodeModal from './components/QRCodeModal';
import BatchGenerator from './components/BatchGenerator';

const QRCodeGenerator = () => {
  const { user } = useAuth();
  const [selectedTrail, setSelectedTrail] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [qrSize, setQrSize] = useState('medium');
  const [errorCorrection, setErrorCorrection] = useState('M');
  const [activeTab, setActiveTab] = useState('single');
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [trails, setTrails] = useState([]);
  const [loading, setLoading] = useState(true);

  const [printSettings, setPrintSettings] = useState({
    paperSize: 'letter',
    orientation: 'portrait',
    layout: 'single',
    quality: 'normal',
    marginTop: '0.5',
    marginBottom: '0.5',
    marginLeft: '0.5',
    marginRight: '0.5',
    includeTitle: true,
    includeInstructions: true,
    includeBranding: false,
    addBorder: true,
    printColor: false
  });

  useEffect(() => {
    const fetchTrails = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const { trails: fetchedTrails, error } = await getTeacherTrails(user?.id);
      if (error) {
        console.error('Error fetching trails:', error);
        setTrails([]);
      } else {
        const transformedTrails = fetchedTrails?.map(trail => ({
          id: trail?.id,
          title: trail?.title,
          description: trail?.description,
          image: trail?.image_url,
          imageAlt: `${trail?.title} cover image`,
          status: trail?.published ? 'Published' : 'Draft',
          classroom: trail?.classrooms?.classroom_name || 'N/A',
          subject: 'General',
          studentsAssigned: 0,
          completionRate: 0,
          minGradeLevel: trail?.min_grade_level || 'All Grades'
        })) || [];
        setTrails(transformedTrails);
      }
      setLoading(false);
    };
    fetchTrails();
  }, [user?.id]);

  const handleTrailSelect = (trail) => {
    setSelectedTrail(trail);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTrail(null);
  };

  const handlePrint = () => {
    setShowPrintPreview(true);
    setTimeout(() => {
      window.print();
      setShowPrintPreview(false);
    }, 500);
  };

  const handleDownload = () => {
    // QRCodeCanvas renders a <canvas> element — find it in the modal
    const canvas = document.querySelector('.fixed canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = `qr-code-${selectedTrail?.id || 'trail'}.png`;
      link.href = canvas?.toDataURL('image/png');
      link?.click();
    }
  };

  const handleBatchGenerate = (selectedTrails) => {
    console.log('Generating batch QR codes for:', selectedTrails);
    alert(`Generating ${selectedTrails?.length} QR codes. This will be compiled into a single PDF document.`);
  };

  return (
    <>
      <Helmet>
        <title>QR Code Generator - LearningTrails</title>
        <meta name="description" content="Generate and print QR codes for learning trails to enable seamless student access" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                QR Code Generator
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Select a learning trail to generate and print its QR code
              </p>
            </div>

            <div className="bg-card rounded-lg shadow-sm mb-6">
              <div className="border-b border-border">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('single')}
                    className={`flex-1 px-6 py-4 text-sm font-medium transition-organic ${
                      activeTab === 'single' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name="QrCode" size={18} className="inline mr-2" />
                    Single QR Code
                  </button>
                  <button
                    onClick={() => setActiveTab('batch')}
                    className={`flex-1 px-6 py-4 text-sm font-medium transition-organic ${
                      activeTab === 'batch' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name="Grid" size={18} className="inline mr-2" />
                    Batch Generation
                  </button>
                </div>
              </div>

              <div className="p-6">
                {loading ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading trails...</p>
                  </div>
                ) : activeTab === 'single' ? (
                  <div>
                    <TrailSelector
                      trails={trails}
                      onTrailSelect={handleTrailSelect}
                      selectedTrail={null}
                    />
                  </div>
                ) : (
                  <BatchGenerator
                    trails={trails}
                    onBatchGenerate={handleBatchGenerate}
                  />
                )}
              </div>
            </div>

            {activeTab === 'single' && !loading && (
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-6 md:p-8">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 bg-accent/20 rounded-full flex items-center justify-center">
                    <Icon name="Lightbulb" size={24} className="text-accent" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                      Quick Tips for QR Code Usage
                    </h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                        <span>Print QR codes on high-quality paper for best scanning results</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                        <span>Place codes at student eye level in well-lit areas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                        <span>Test scanning before distributing to ensure functionality</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                        <span>Include clear instructions for students who are new to QR scanning</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* QR Code Modal */}
      {showModal && selectedTrail && (
        <QRCodeModal
          trail={selectedTrail}
          onClose={handleCloseModal}
          qrSize={qrSize}
          onSizeChange={setQrSize}
          errorCorrection={errorCorrection}
          onErrorCorrectionChange={setErrorCorrection}
          printSettings={printSettings}
          onPrintSettingsChange={setPrintSettings}
          onPrint={handlePrint}
          onDownload={handleDownload}
        />
      )}

      {showPrintPreview && (
        <style>
          {`
            @media print {
              body * { visibility: hidden; }
              .print-area, .print-area * { visibility: visible; }
              .print-area { position: absolute; left: 0; top: 0; }
            }
          `}
        </style>
      )}
    </>
  );
};

export default QRCodeGenerator;